import React, { useEffect, useState } from "react";
import api from "../api";
import { Save, Sparkles, Plus, Trash2 } from "lucide-react";

/**
 * Settings widget for the public report:
 *   - `phase2_collectors_enabled` — on = fire on-page/competitors/AI-test/etc.
 *   - `reportReviews[]` — up to 5 client reviews shown on the CTA section
 *   - `competitor_blocklist[]` — extra domains to exclude from competitor lists
 */
export default function ReportV2Settings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.get("/settings").then(r => {
      const s = r.data || {};
      setState({
        phase2_collectors_enabled: s.phase2_collectors_enabled !== false, // default true
        reportReviews: Array.isArray(s.reportReviews) ? s.reportReviews : [],
        competitor_blocklist: Array.isArray(s.competitor_blocklist) ? s.competitor_blocklist : [],
      });
    });
  };

  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/settings", state);
      setMsg({ ok: true, text: "Impostazioni salvate." });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!state) return null;

  const updateReview = (i, patch) => {
    const arr = [...state.reportReviews];
    arr[i] = { ...arr[i], ...patch };
    setState({ ...state, reportReviews: arr });
  };
  const removeReview = (i) => {
    const arr = state.reportReviews.filter((_, idx) => idx !== i);
    setState({ ...state, reportReviews: arr });
  };
  const addReview = () => {
    if (state.reportReviews.length >= 5) return;
    setState({ ...state, reportReviews: [...state.reportReviews, { text: "", author: "" }] });
  };

  return (
    <div className="card" data-testid="report-v2-settings">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} color="var(--mv-primary)"/>
        <div className="h-section" style={{margin:0}}>Report pubblico</div>
      </div>

      <label className="flex items-center gap-2 mb-3"
             data-testid="v2-toggle-phase2">
        <input type="checkbox" checked={state.phase2_collectors_enabled}
               onChange={e => setState({...state, phase2_collectors_enabled: e.target.checked})}/>
        <span><strong>Raccolta dati Fase 2</strong> — on-page, competitor, keyword volumes, AI Overview, robots.txt, JSON-LD. Costo extra ~$1.50/analisi.</span>
      </label>

      <div className="mb-3">
        <div className="text-small text-muted mb-2"><strong>Recensioni</strong> (max 5, mostrate nella sezione CTA del report v2)</div>
        {state.reportReviews.map((r, i) => (
          <div key={i} className="flex gap-2 items-start mb-2" data-testid={`v2-review-${i}`}>
            <textarea className="input" rows={2} style={{flex:1}}
              placeholder="Testo della recensione..." value={r.text || ""}
              onChange={e => updateReview(i, {text: e.target.value})}/>
            <input className="input" style={{maxWidth:200}}
              placeholder="Autore" value={r.author || ""}
              onChange={e => updateReview(i, {author: e.target.value})}/>
            <button className="btn btn-ghost" onClick={() => removeReview(i)}
              title="Rimuovi" data-testid={`v2-review-del-${i}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
        {state.reportReviews.length < 5 && (
          <button className="btn btn-ghost" onClick={addReview}
            data-testid="v2-review-add">
            <Plus size={14}/> Aggiungi recensione
          </button>
        )}
      </div>

      <div className="mb-3">
        <div className="text-small text-muted mb-1"><strong>Blocklist competitor</strong> (domini extra da escludere, uno per riga)</div>
        <textarea className="input" rows={3}
          value={(state.competitor_blocklist || []).join("\n")}
          onChange={e => setState({
            ...state,
            competitor_blocklist: e.target.value.split("\n").map(x => x.trim()).filter(Boolean),
          })}
          data-testid="v2-blocklist"
          placeholder="esempio-marketplace.com&#10;altrogigante.it"/>
        <div className="text-small text-muted">
          I domini standard (Wikipedia, Amazon, PagineGialle, ecc.) sono già esclusi automaticamente.
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn btn-primary"
              data-testid="v2-settings-save">
        <Save size={14}/> {saving ? "Salvataggio…" : "Salva impostazioni v2"}
      </button>
      {msg && (
        <span className={`badge ${msg.ok ? "badge-green" : "badge-red"}`}
              style={{marginLeft: 8}} data-testid="v2-settings-msg">{msg.text}</span>
      )}
    </div>
  );
}
